import React, { useContext, useEffect, useMemo, useState } from 'react';
import { tenantService, TenantDto, TenantCreateDto, TenantUpdateDto } from '../services/tenantService';
import { UserContext } from '../contexts/UserContext';

type Mode = 'list' | 'create' | 'edit';

const emptyCreate: TenantCreateDto = {
  vendorId: 1,
  vendorTenantId: 0,
  tenantName: '',
  tenantDescription: '',
  enabled: true,
  endpointUrl: '',
  acornTenantSlug: '',
  clientId: '',
  clientSecret: '',
};

export default function AdminTenantsPage() {
  const { userProfile, hasClaim } = useContext(UserContext);
  const mainRole = (userProfile?.mainRole || '').toLowerCase();
  const isAdmin = mainRole === 'admin' || mainRole === 'acornjradmin' || hasClaim('role', 'Admin') || hasClaim('role', 'AcornJrAdmin');

  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [mode, setMode] = useState<Mode>('list');
  const [selected, setSelected] = useState<TenantDto | null>(null);
  const [form, setForm] = useState<TenantCreateDto>(emptyCreate);
  const [showConfirm, setShowConfirm] = useState<{ id: number | null; name: string } | null>(null);
  const [filterText, setFilterText] = useState<string>('');
  const [filterEnabled, setFilterEnabled] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [page, setPage] = useState<number>(1);
  const pageSize = 10;

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await tenantService.list();
      setTenants(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setForm(emptyCreate);
    setSelected(null);
    setMode('create');
  };

  const startEdit = (t: TenantDto) => {
    setSelected(t);
    const update: TenantUpdateDto & Partial<TenantCreateDto> = {
      tenantName: t.tenantName,
      tenantDescription: t.tenantDescription ?? '',
      enabled: t.enabled,
      endpointUrl: t.endpointUrl ?? '',
      acornTenantSlug: t.acornTenantSlug ?? '',
      clientId: t.clientId,
    };
    setForm({
      vendorId: t.vendorId,
      vendorTenantId: t.vendorTenantId,
      tenantName: update.tenantName || '',
      tenantDescription: update.tenantDescription || '',
      enabled: update.enabled,
      endpointUrl: update.endpointUrl || '',
      acornTenantSlug: update.acornTenantSlug || '',
      clientId: update.clientId || '',
      clientSecret: '',
    });
    setMode('edit');
  };

  const cancel = () => {
    setMode('list');
    setSelected(null);
    setForm(emptyCreate);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: name === 'vendorId' || name === 'vendorTenantId' ? Number(value) : value }));
  };

  const save = async () => {
    setLoading(true);
    setError('');
    try {
      if (mode === 'create') {
        await tenantService.create(form);
      } else if (mode === 'edit' && selected) {
        const update: TenantUpdateDto = {
          tenantName: form.tenantName,
          tenantDescription: form.tenantDescription,
          enabled: form.enabled,
          endpointUrl: form.endpointUrl,
          acornTenantSlug: form.acornTenantSlug,
          clientId: form.clientId,
          clientSecret: form.clientSecret || undefined,
        };
        await tenantService.update(selected.tenantId, update);
      }
      await load();
      cancel();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const enable = async (id: number) => {
    setLoading(true);
    setError('');
    try {
      await tenantService.enable(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Enable failed');
    } finally {
      setLoading(false);
    }
  };

  const disable = async (id: number) => {
    setLoading(true);
    setError('');
    try {
      await tenantService.disable(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Disable failed');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id: number) => {
    setLoading(true);
    setError('');
    try {
      await tenantService.delete(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return tenants.filter(t => {
      const matchText = !q || [
        t.tenantName,
        String(t.vendorId),
        String(t.vendorTenantId),
        t.clientId,
        t.endpointUrl || '',
        t.acornTenantSlug || '',
      ].some(v => String(v).toLowerCase().includes(q));
      const matchEnabled = filterEnabled === 'all' || (filterEnabled === 'enabled' ? t.enabled : !t.enabled);
      return matchText && matchEnabled;
    });
  }, [tenants, filterText, filterEnabled]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => a.tenantName.localeCompare(b.tenantName));
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page]);

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-yellow-100 text-yellow-800 p-3 rounded">You are not authorized to manage tenants.</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Tenant Management</h1>
        <button className="btn btn-primary bg-blue-600 text-white px-3 py-2 rounded" onClick={startCreate}>New Tenant</button>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-3">{error}</div>
      )}

      {mode === 'list' && (
        <div className="overflow-x-auto">
          <div className="flex flex-wrap gap-2 mb-3">
            <input
              placeholder="Search tenants..."
              className="border p-2 rounded w-64"
              value={filterText}
              onChange={(e) => { setFilterText(e.target.value); setPage(1); }}
            />
            <select
              className="border p-2 rounded"
              value={filterEnabled}
              onChange={(e) => { setFilterEnabled(e.target.value as any); setPage(1); }}
            >
              <option value="all">All</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="p-2">Name</th>
                <th className="p-2">Vendor</th>
                <th className="p-2">Vendor Tenant</th>
                <th className="p-2">Enabled</th>
                <th className="p-2">ClientId</th>
                <th className="p-2">Updated</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(t => (
                <tr key={t.tenantId} className="border-b">
                  <td className="p-2">{t.tenantName}</td>
                  <td className="p-2">{t.vendorId}</td>
                  <td className="p-2">{t.vendorTenantId}</td>
                  <td className="p-2">{t.enabled ? 'Yes' : 'No'}</td>
                  <td className="p-2">{t.clientId}</td>
                  <td className="p-2">{new Date(t.updatedAt).toLocaleString()}</td>
                  <td className="p-2 space-x-2">
                    <button className="px-2 py-1 bg-gray-200 rounded" onClick={() => startEdit(t)}>Edit</button>
                    {t.enabled ? (
                      <button className="px-2 py-1 bg-yellow-200 rounded" onClick={() => disable(t.tenantId)}>Disable</button>
                    ) : (
                      <button className="px-2 py-1 bg-green-200 rounded" onClick={() => enable(t.tenantId)}>Enable</button>
                    )}
                    <button className="px-2 py-1 bg-red-300 rounded" onClick={() => setShowConfirm({ id: t.tenantId, name: t.tenantName })}>Delete</button>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-4 text-center text-gray-500">No tenants</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="flex items-center gap-2 mt-3">
            <button
              className="px-2 py-1 border rounded"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span className="text-sm">Page {page} / {totalPages}</span>
            <button
              className="px-2 py-1 border rounded"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {mode !== 'list' && (
        <div className="max-w-3xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col">
              <span className="text-xs text-gray-500">Tenant Name</span>
              <input className="border p-2 rounded" name="tenantName" value={form.tenantName} onChange={onChange} />
              {!form.tenantName && <span className="text-xs text-red-600 mt-1">Name is required</span>}
            </label>
            <label className="flex flex-col">
              <span className="text-xs text-gray-500">Description</span>
              <input className="border p-2 rounded" name="tenantDescription" value={form.tenantDescription || ''} onChange={onChange} />
            </label>
            <label className="flex flex-col">
              <span className="text-xs text-gray-500">Vendor Id</span>
              <input className="border p-2 rounded" name="vendorId" type="number" value={form.vendorId} onChange={onChange} />
              {(!form.vendorId && form.vendorId !== 0) && <span className="text-xs text-red-600 mt-1">Vendor Id is required</span>}
            </label>
            <label className="flex flex-col">
              <span className="text-xs text-gray-500">Vendor Tenant Id</span>
              <input className="border p-2 rounded" name="vendorTenantId" type="number" value={form.vendorTenantId} onChange={onChange} />
              {(!form.vendorTenantId && form.vendorTenantId !== 0) && <span className="text-xs text-red-600 mt-1">Vendor Tenant Id is required</span>}
            </label>
            <label className="flex flex-col md:col-span-2">
              <span className="text-xs text-gray-500">Endpoint Url</span>
              <input className="border p-2 rounded" name="endpointUrl" value={form.endpointUrl || ''} onChange={onChange} />
            </label>
            <label className="flex flex-col">
              <span className="text-xs text-gray-500">Acorn Tenant Slug</span>
              <input className="border p-2 rounded" name="acornTenantSlug" value={form.acornTenantSlug || ''} onChange={onChange} />
            </label>
            <label className="flex flex-col">
              <span className="text-xs text-gray-500">Client Id</span>
              <input className="border p-2 rounded" name="clientId" value={form.clientId} onChange={onChange} />
              {!form.clientId && <span className="text-xs text-red-600 mt-1">Client Id is required</span>}
            </label>
            {mode === 'create' ? (
              <label className="flex flex-col">
                <span className="text-xs text-gray-500">Client Secret</span>
                <input className="border p-2 rounded" name="clientSecret" value={form.clientSecret} onChange={onChange} />
                {!form.clientSecret && <span className="text-xs text-red-600 mt-1">Client Secret is required</span>}
              </label>
            ) : (
              <div className="flex items-center gap-2 mt-6">
                <button
                  type="button"
                  className="px-2 py-1 border rounded"
                  onClick={() => setForm(prev => ({ ...prev, clientSecret: '' }))}
                >
                  Rotate Client Secret
                </button>
                {form.clientSecret && (
                  <input
                    className="border p-2 rounded"
                    placeholder="New Client Secret"
                    name="clientSecret"
                    value={form.clientSecret}
                    onChange={onChange}
                  />
                )}
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              className="px-3 py-2 bg-blue-600 text-white rounded"
              disabled={loading || !form.tenantName || !form.clientId || (mode === 'create' && !form.clientSecret)}
              onClick={save}
            >
              {mode === 'create' ? 'Create' : 'Save'}
            </button>
            <button className="px-3 py-2 bg-gray-300 rounded" onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
          <div className="bg-white p-4 rounded shadow w-96">
            <div className="font-semibold mb-2">Delete Tenant</div>
            <div className="text-sm mb-4">Are you sure you want to delete "{showConfirm.name}"? This action cannot be undone.</div>
            <div className="flex gap-2 justify-end">
              <button className="px-3 py-2 bg-gray-200 rounded" onClick={() => setShowConfirm(null)}>Cancel</button>
              <button className="px-3 py-2 bg-red-600 text-white rounded" onClick={() => { const id = showConfirm.id!; setShowConfirm(null); remove(id); }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
